import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faAngleDown, faPlus, faThumbTack } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";

import { EntityType, PinnedFieldsVisibility } from "@mapmycustomers/shared";
import RoleEnum from "@mapmycustomers/shared/enum/Role";
import Visibility from "@mapmycustomers/shared/enum/Visibility";
import { EntityTypeSupportingPreview } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import Role from "@mapmycustomers/shared/types/Role";
import Team from "@mapmycustomers/shared/types/Team";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, EntityFieldSelector, VisibilitySelector } from "@mapmycustomers/ui";

import { getRole } from "@app/store/iam";
import { getTeams } from "@app/store/members";
import { updateRecordsPreviewConfiguration } from "@app/store/recordPreview/actions";
import { InternalFieldDescription } from "@app/store/recordPreview/EntityColumnsConfiguration";
import EntityColumnsConfiguration from "@app/store/recordPreview/EntityColumnsConfiguration";
import {
  getRecordPreviewConfiguration,
  isRecordPreviewConfigurationLoading,
} from "@app/store/recordPreview/selectors";
import { RootState } from "@app/store/rootReducer";
import companyFieldModel from "@app/util/fieldModel/CompanyFieldModel";
import dealFieldModel from "@app/util/fieldModel/DealFieldModel";
import personFieldModel from "@app/util/fieldModel/PersonFieldModel";

import SettingPage from "../common/SettingPage";

import Details from "./component/Details";
import EntityFields from "./component/EntityFields";
import messages from "./messages";
import styles from "./PinnedFields.module.scss";
import { ALL_VISIBILITIES } from "./util/useVisibilityOptions";

const entityFieldModels = {
  [EntityType.COMPANY]: companyFieldModel,
  [EntityType.DEAL]: dealFieldModel,
  [EntityType.PERSON]: personFieldModel,
};

interface Props {
  configuration: EntityColumnsConfiguration;
  loading: boolean;
  onUpdatePinnedFields: typeof updateRecordsPreviewConfiguration.request;
  role: Role;
  teams: Team[];
}

const PinnedFields: React.FC<Props> = ({
  configuration,
  loading,
  onUpdatePinnedFields,
  role,
  teams,
}) => {
  const intl = useIntl();
  const [hasChanges, setChanges, unsetChanges] = useBoolean();

  const [details, setDetails] = useState<
    Pick<EntityColumnsConfiguration, "actions" | "engagement">
  >({
    actions: configuration.actions,
    engagement: configuration.engagement,
  });

  const [companyFields, setCompanyFields] = useState(configuration[EntityType.COMPANY]);
  const [dealFields, setDealFields] = useState(configuration[EntityType.DEAL]);
  const [personFields, setPersonFields] = useState(configuration[EntityType.PERSON]);

  const entityFieldsSetters = useMemo(
    () => ({
      [EntityType.COMPANY]: setCompanyFields,
      [EntityType.DEAL]: setDealFields,
      [EntityType.PERSON]: setPersonFields,
    }),
    []
  );

  const addedFields = useMemo(
    () => [...companyFields, ...dealFields, ...personFields].map(({ field }) => field),
    [companyFields, dealFields, personFields]
  );

  const selectedPinnedFields = useMemo(
    () => ({
      [EntityType.COMPANY]: companyFields.filter(({ selected }) => selected),
      [EntityType.DEAL]: dealFields.filter(({ selected }) => selected),
      [EntityType.PERSON]: personFields.filter(({ selected }) => selected),
    }),
    [companyFields, dealFields, personFields]
  );

  const isAnyFieldSelected =
    selectedPinnedFields[EntityType.COMPANY].length > 0 ||
    selectedPinnedFields[EntityType.PERSON].length > 0 ||
    selectedPinnedFields[EntityType.DEAL].length > 0;

  const handleSaveChanges = useCallback(() => {
    const actions =
      details.actions.visibility === Visibility.SHARED_WITH_TEAM && !details.actions.teamIds?.length
        ? configuration.actions
        : details.actions;
    const engagement =
      details.engagement.visibility === Visibility.SHARED_WITH_TEAM &&
      !details.engagement.teamIds?.length
        ? configuration.engagement
        : details.engagement;

    onUpdatePinnedFields({
      configuration: {
        actions,
        engagement,
        [EntityType.COMPANY]: companyFields,
        [EntityType.DEAL]: dealFields,
        [EntityType.PERSON]: personFields,
      },
      successCallback: () => {
        unsetChanges();
      },
    });
  }, [
    companyFields,
    configuration.actions,
    configuration.engagement,
    dealFields,
    details.actions,
    details.engagement,
    onUpdatePinnedFields,
    personFields,
    unsetChanges,
  ]);

  const handleUndo = useCallback(() => {
    setCompanyFields(configuration[EntityType.COMPANY]);
    setPersonFields(configuration[EntityType.PERSON]);
    setDealFields(configuration[EntityType.DEAL]);
    setDetails({
      actions: configuration.actions,
      engagement: configuration.engagement,
    });
    unsetChanges();
  }, [configuration, unsetChanges]);

  const handleEntityFieldsChange = useCallback(
    (
      fields: InternalFieldDescription[],
      entityType: EntityTypeSupportingPreview,
      dataChanged: boolean = true
    ) => {
      entityFieldsSetters[entityType](fields);
      if (dataChanged) {
        setChanges();
      }
    },
    [entityFieldsSetters, setChanges]
  );

  const updateVisibility = useCallback(
    (visibility: PinnedFieldsVisibility, teamIds?: Team["id"][]) =>
      (field: InternalFieldDescription) => {
        if (field.selected) {
          return { ...field, selected: false, teamIds, visibility };
        }
        return field;
      },
    []
  );

  const handleVisibilityChange = useCallback(
    (visibility: PinnedFieldsVisibility, teamIds?: Team["id"][]) => {
      setChanges();
      if (selectedPinnedFields[EntityType.COMPANY].length) {
        setCompanyFields((companyFields) =>
          companyFields.map(updateVisibility(visibility, teamIds))
        );
      }
      if (selectedPinnedFields[EntityType.DEAL].length) {
        setDealFields((dealFields) => dealFields.map(updateVisibility(visibility, teamIds)));
      }
      if (selectedPinnedFields[EntityType.PERSON].length) {
        setPersonFields((personFields) => personFields.map(updateVisibility(visibility, teamIds)));
      }
    },
    [selectedPinnedFields, setChanges, updateVisibility]
  );

  const filterSelectedFields = useCallback((existingFields: InternalFieldDescription[]) => {
    const disabledFields = existingFields.filter((field) => field.disabled);
    const startDisplayOrder = (disabledFields.at(-1)?.displayOrder ?? -1) + 1;
    const filteredFields = existingFields
      .filter((field) => !field.disabled && !field.selected)
      .map((field, index) => ({ ...field, displayOrder: startDisplayOrder + index }));
    return [...disabledFields, ...filteredFields];
  }, []);

  const handleRemove = useCallback(() => {
    setChanges();
    if (selectedPinnedFields[EntityType.COMPANY].length) {
      setCompanyFields((fields) => filterSelectedFields(fields));
    }
    if (selectedPinnedFields[EntityType.DEAL].length) {
      setDealFields((fields) => filterSelectedFields(fields));
    }
    if (selectedPinnedFields[EntityType.PERSON].length) {
      setPersonFields((fields) => filterSelectedFields(fields));
    }
  }, [filterSelectedFields, selectedPinnedFields, setChanges]);

  const handleAddField = useCallback(
    (field: IField, entityType: EntityTypeSupportingPreview) => {
      setChanges();
      entityFieldsSetters[entityType]((existingFields) => [
        ...existingFields,
        {
          disabled: false,
          displayOrder: (existingFields.at(-1)?.displayOrder ?? -1) + 1,
          field,
          visibility: Visibility.PRIVATE,
        },
      ]);
    },
    [entityFieldsSetters, setChanges]
  );

  const handleDetailsChange = useCallback(
    (details: Pick<EntityColumnsConfiguration, "actions" | "engagement">) => {
      setChanges();
      setDetails(details);
    },
    [setChanges]
  );

  useEffect(() => {
    // After save
    setCompanyFields(configuration[EntityType.COMPANY]);
    setPersonFields(configuration[EntityType.PERSON]);
    setDealFields(configuration[EntityType.DEAL]);
    setDetails({
      actions: configuration.actions,
      engagement: configuration.engagement,
    });
  }, [configuration]);

  return (
    <SettingPage
      changesSaving={loading}
      hasChanges={hasChanges}
      onSaveChanges={handleSaveChanges}
      onUnDo={handleUndo}
      title={intl.formatMessage(messages.title)}
    >
      <div className={styles.container}>
        <div className={styles.subTitle}>
          {intl.formatMessage(messages.description)}
          <EntityFieldSelector
            addedFields={addedFields}
            entityFieldModels={entityFieldModels}
            onChange={handleAddField}
          >
            <Button
              leftIcon={<FontAwesomeIcon icon={faPlus} />}
              rightIcon={<FontAwesomeIcon icon={faAngleDown} />}
              type="primary"
            >
              {intl.formatMessage(messages.addField)}
            </Button>
          </EntityFieldSelector>
        </div>
        {isAnyFieldSelected && (
          <div className={styles.control}>
            {role?.key === RoleEnum.OWNER && (
              <VisibilitySelector
                allowedVisibilities={ALL_VISIBILITIES}
                onChange={handleVisibilityChange}
                subLabel={intl.formatMessage(messages.pinFor)}
                teams={teams}
                userRole={role?.key}
              >
                <Button leftIcon={<FontAwesomeIcon icon={faThumbTack} />} type="filled">
                  {intl.formatMessage(messages.manage)}
                </Button>
              </VisibilitySelector>
            )}
            <Button danger onClick={handleRemove}>
              {intl.formatMessage(messages.remove)}
            </Button>
          </div>
        )}
        <EntityFields
          entityType={EntityType.COMPANY}
          fields={companyFields}
          onChange={handleEntityFieldsChange}
        />
        <EntityFields
          entityType={EntityType.PERSON}
          fields={personFields}
          onChange={handleEntityFieldsChange}
        />
        <EntityFields
          entityType={EntityType.DEAL}
          fields={dealFields}
          onChange={handleEntityFieldsChange}
        />
        <Details details={details} onChange={handleDetailsChange} />
      </div>
    </SettingPage>
  );
};

const mapStateToProps = (state: RootState) => ({
  configuration: getRecordPreviewConfiguration(state),
  loading: isRecordPreviewConfigurationLoading(state),
  role: getRole(state)!,
  teams: getTeams(state),
});

const mapDispatchToProps = {
  onUpdatePinnedFields: updateRecordsPreviewConfiguration.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(PinnedFields);
